import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PhoneIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Input, Pagination } from '@/components/common';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { getErrorMessage } from '@/services';
import {
  directionsUrl,
  hospitalNetworkApi,
  isPincodeQuery,
  networkBadge,
  type LocatorKind,
  type LocatorProvider,
} from '@/services/hospitalNetwork';
import { useAuthStore } from '@/store/auth';

/**
 * DQ-346: the cashless network locator -- search by name, city and pincode
 * (like the #392 garage list), filtered to the insurer of the customer's health
 * policy. `kind` keeps it from being hospital-only: the garage list (#392) can
 * render through the same component.
 */

const PAGE_SIZE = 20;
const ANY_INSURER = '';

interface ProviderLocatorProps {
  kind?: LocatorKind;
  /** When given, each card gets a "Choose" button (the claim form's picker). */
  onSelect?: (provider: LocatorProvider) => void;
  selectLabel?: string;
  /** Preselect this insurer instead of the customer's own. */
  defaultInsurerId?: string;
}

const NOUN: Record<LocatorKind, { one: string; many: string }> = {
  hospital: { one: 'hospital', many: 'hospitals' },
  garage: { one: 'garage', many: 'garages' },
};

function badgeVariant(provider: LocatorProvider): 'success' | 'info' | 'primary' {
  if (provider.network_scope === 'all') return 'success';
  if (provider.network_scope === 'unique') return 'primary';
  return 'info';
}

function ProviderCard({
  provider,
  onSelect,
  selectLabel,
}: {
  provider: LocatorProvider;
  onSelect?: (provider: LocatorProvider) => void;
  selectLabel: string;
}) {
  const badge = networkBadge(provider);
  const insurers = provider.cashless_insurers ?? [];
  return (
    <li className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white break-words">{provider.name}</h3>
            {badge && (
              <Badge size="sm" variant={badgeVariant(provider)}>
                {badge}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 flex items-start gap-1">
            <MapPinIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              {[provider.address, provider.city_name, provider.pincode].filter(Boolean).join(', ')}
            </span>
          </p>
          {provider.phone && (
            <a
              href={`tel:${provider.phone}`}
              className="mt-1 inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400"
            >
              <PhoneIcon className="w-4 h-4" />
              {provider.phone}
            </a>
          )}
          {insurers.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Cashless with:</span>
              {insurers.map((insurer) => (
                <Badge key={insurer.id} size="sm" variant="default">
                  {insurer.name}
                </Badge>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            {provider.emergency_available && (
              <span className="inline-flex items-center gap-1">
                <BuildingOffice2Icon className="w-4 h-4" /> 24x7 emergency
              </span>
            )}
            {provider.ambulance_available && (
              <span className="inline-flex items-center gap-1">
                <TruckIcon className="w-4 h-4" /> Ambulance
              </span>
            )}
            {typeof provider.distance === 'number' && <span>{provider.distance} km away</span>}
          </div>
        </div>
        <div className="flex sm:flex-col gap-2 flex-shrink-0">
          <a
            href={directionsUrl(provider)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Directions
          </a>
          {onSelect && (
            <Button size="sm" onClick={() => onSelect(provider)}>
              {selectLabel}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

export function ProviderLocator({
  kind = 'hospital',
  onSelect,
  selectLabel = 'Choose',
  defaultInsurerId,
}: ProviderLocatorProps) {
  const noun = NOUN[kind];
  const { user, isAuthenticated } = useAuthStore();
  const isCustomer = isAuthenticated && user?.role === 'customer';

  const name = useDebouncedSearch('', 300);
  const city = useDebouncedSearch('', 300);
  const pincode = useDebouncedSearch('', 300);
  const [insurer, setInsurer] = useState<string | null>(defaultInsurerId ?? null);
  const [cashlessOnly, setCashlessOnly] = useState(true);
  const [page, setPage] = useState(1);

  const pincodeInvalid = !isPincodeQuery(pincode.term.trim());

  const { data: networkInsurers = [] } = useQuery({
    queryKey: ['locator-network-insurers'],
    queryFn: () => hospitalNetworkApi.networkInsurers(),
    enabled: kind === 'hospital',
    staleTime: 5 * 60 * 1000,
  });
  const { data: myInsurers, isFetched: myInsurersFetched } = useQuery({
    queryKey: ['locator-my-insurers'],
    queryFn: () => hospitalNetworkApi.myInsurers(),
    enabled: kind === 'hospital' && isCustomer,
    staleTime: 5 * 60 * 1000,
  });

  // The customer's most recent health policy's insurer, switchable (SPEC-346 Q14).
  useEffect(() => {
    if (insurer !== null) return;
    if (!isCustomer || kind !== 'hospital') {
      setInsurer(ANY_INSURER);
      return;
    }
    if (myInsurersFetched) setInsurer(myInsurers?.[0]?.id ?? ANY_INSURER);
  }, [insurer, isCustomer, kind, myInsurers, myInsurersFetched]);

  const insurerOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const item of [...(myInsurers ?? []), ...networkInsurers]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      options.push({ value: item.id, label: item.name });
    }
    return options;
  }, [myInsurers, networkInsurers]);

  const filters = {
    q: name.debouncedTerm,
    city: city.debouncedTerm,
    pincode: pincode.debouncedTerm,
    insurer: insurer || undefined,
    cashless: cashlessOnly,
    page,
  };

  useEffect(() => {
    setPage(1);
  }, [name.debouncedTerm, city.debouncedTerm, pincode.debouncedTerm, insurer, cashlessOnly]);

  const ready = insurer !== null && !pincodeInvalid && isPincodeQuery(pincode.debouncedTerm.trim());
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['provider-locator', kind, filters],
    queryFn: () => hospitalNetworkApi.search(kind, filters),
    enabled: ready,
    placeholderData: (previous) => previous,
  });

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input
          name="locator_name"
          label={`${noun.one[0].toUpperCase()}${noun.one.slice(1)} name`}
          placeholder={`Search ${noun.many} by name`}
          value={name.term}
          onChange={(e) => name.setTerm(e.target.value)}
        />
        <Input
          name="locator_city"
          label="City"
          placeholder="e.g. Pune"
          value={city.term}
          onChange={(e) => city.setTerm(e.target.value)}
        />
        <Input
          name="locator_pincode"
          label="Pincode"
          placeholder="6 digits, or the first few"
          inputMode="numeric"
          value={pincode.term}
          error={pincodeInvalid ? 'Enter up to 6 digits.' : undefined}
          onChange={(e) => pincode.setTerm(e.target.value)}
        />
        {kind === 'hospital' && (
          <div>
            <label htmlFor="locator_insurer" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Insurer
            </label>
            <select
              id="locator_insurer"
              value={insurer ?? ANY_INSURER}
              onChange={(e) => setInsurer(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value={ANY_INSURER}>Any insurer</option>
              {insurerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={cashlessOnly}
          onChange={(e) => setCashlessOnly(e.target.checked)}
          className="rounded border-gray-300"
        />
        Cashless only
      </label>

      {kind === 'hospital' && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Network lists change; confirm cashless with the hospital and your insurer before admission.
        </p>
      )}

      {error ? (
        <div role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="text-sm text-red-700 dark:text-red-300">
            <p>The {noun.many} could not be loaded: {getErrorMessage(error)}</p>
            <button type="button" className="mt-1 underline" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        </div>
      ) : !ready || isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading {noun.many}…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            No network {noun.one} found for these filters.
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {kind === 'hospital'
              ? 'Call your insurer helpline or try a nearby pincode. Clearing the city or choosing "Any insurer" widens the search.'
              : 'Try a nearby pincode or clear the city.'}
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
            {total} {total === 1 ? noun.one : noun.many} found{isFetching ? ' (updating…)' : ''}
          </p>
          <ul className="space-y-3">
            {rows.map((provider) => (
              <ProviderCard
                key={provider.provider_id}
                provider={provider}
                onSelect={onSelect}
                selectLabel={selectLabel}
              />
            ))}
          </ul>
          {total > PAGE_SIZE && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / PAGE_SIZE)}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}
